import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';

export const FeedbackSection = () => {
  const [formData, setFormData] = useState({ email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '257ab86b-e817-4aa2-89b2-54cfc767ed7a',
          email: formData.email,
          message: formData.message,
          subject: 'New Feedback from Calorium Website',
        }),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ email: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <Box sx={{ py: { xs: 8, md: 10 } }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            py: { xs: 6, md: 8 },
            px: { xs: 3, md: 6 },
            backgroundColor: 'background.paper',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                textAlign: 'center',
              }}
            >
              We'd Love to Hear From You
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 4,
                textAlign: 'center',
              }}
            >
              Have a suggestion, feedback, or question? Send us a message and we'll get back to you.
            </Typography>

            {status === 'success' ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Thank you! Your message has been sent successfully.
              </Alert>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
              >
                <TextField
                  type="email"
                  label="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
                <TextField
                  label="Your Message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  fullWidth
                  multiline
                  rows={4}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
                {status === 'error' && (
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    Something went wrong. Please try again.
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={status === 'loading'}
                  endIcon={status === 'loading' ? <CircularProgress size={20} color="inherit" /> : <Icon icon="solar:plain-bold" width={20} />}
                  sx={{
                    py: 1.75,
                    alignSelf: 'center',
                    px: 5,
                    backgroundColor: '#36C65A',
                    '&:hover': {
                      backgroundColor: '#2BA84A',
                    },
                  }}
                >
                  {status === 'loading' ? 'Sending...' : 'Send Message'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
